import React from "react";
import { Link } from "react-router-dom";
import { BarChart3, Home } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useI18n } from "@/contexts/I18nContext";

export default function NotFound() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-app grid-lines relative">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(800px 500px at 50% -20%, rgba(255,23,68,0.06), transparent 70%)"
        }}
      />

      <TopBar />

      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-56px)] px-4 py-12">
        <div className="w-full max-w-lg text-center mob-page" style={{ animation: "page-in 0.4s ease-out" }}>

          {/* Broken chart visual */}
          <div className="mb-8 flex justify-center">
            <div className="relative" style={{ width: 200, height: 120 }}>
              {/* Grid lines */}
              <svg width="200" height="120" className="absolute inset-0" style={{ opacity: 0.15 }}>
                <line x1="0" y1="30" x2="200" y2="30" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
                <line x1="0" y1="60" x2="200" y2="60" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
                <line x1="0" y1="90" x2="200" y2="90" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
                <line x1="50" y1="0" x2="50" y2="120" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
                <line x1="100" y1="0" x2="100" y2="120" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
                <line x1="150" y1="0" x2="150" y2="120" stroke="var(--iq-border-bright)" strokeWidth="0.5" />
              </svg>

              {/* Candlestick going up then crashing */}
              <svg width="200" height="120" className="relative">
                {/* Uptrend candles */}
                <rect x="20" y="70" width="8" height="30" rx="1" fill="var(--iq-green)" opacity="0.7" />
                <line x1="24" y1="65" x2="24" y2="70" stroke="var(--iq-green)" strokeWidth="1.5" opacity="0.7" />
                <line x1="24" y1="100" x2="24" y2="100" stroke="var(--iq-green)" strokeWidth="1.5" opacity="0.7" />

                <rect x="45" y="55" width="8" height="35" rx="1" fill="var(--iq-green)" opacity="0.8" />
                <line x1="49" y1="48" x2="49" y2="55" stroke="var(--iq-green)" strokeWidth="1.5" opacity="0.8" />
                <line x1="49" y1="90" x2="49" y2="90" stroke="var(--iq-green)" strokeWidth="1.5" opacity="0.8" />

                <rect x="70" y="40" width="8" height="30" rx="1" fill="var(--iq-green)" />
                <line x1="74" y1="33" x2="74" y2="40" stroke="var(--iq-green)" strokeWidth="1.5" />
                <line x1="74" y1="70" x2="74" y2="70" stroke="var(--iq-green)" strokeWidth="1.5" />

                {/* Trend line */}
                <line x1="15" y1="85" x2="78" y2="55" stroke="var(--iq-green)" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.5" />

                {/* The crash candle */}
                <rect x="95" y="75" width="8" height="35" rx="1" fill="var(--iq-red)" />
                <line x1="99" y1="70" x2="99" y2="75" stroke="var(--iq-red)" strokeWidth="1.5" />
                <line x1="99" y1="110" x2="99" y2="110" stroke="var(--iq-red)" strokeWidth="1.5" />

                {/* Crash continuation */}
                <rect x="120" y="85" width="8" height="30" rx="1" fill="var(--iq-red)" opacity="0.8" />
                <line x1="124" y1="80" x2="124" y2="85" stroke="var(--iq-red)" strokeWidth="1.5" opacity="0.8" />
                <line x1="124" y1="115" x2="124" y2="115" stroke="var(--iq-red)" strokeWidth="1.5" opacity="0.8" />

                {/* Final drop */}
                <rect x="145" y="95" width="8" height="20" rx="1" fill="var(--iq-red)" opacity="0.6" />
                <line x1="149" y1="92" x2="149" y2="95" stroke="var(--iq-red)" strokeWidth="1.5" opacity="0.6" />
                <line x1="149" y1="115" x2="149" y2="115" stroke="var(--iq-red)" strokeWidth="1.5" opacity="0.6" />

                {/* Trend line down */}
                <line x1="78" y1="55" x2="153" y2="100" stroke="var(--iq-red)" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.5" />

                {/* Crash arrow */}
                <text x="130" y="55" fill="var(--iq-red)" fontSize="16" fontWeight="bold" opacity="0.6">↘</text>

                {/* 404 label on chart */}
                <text x="82" y="28" fill="var(--iq-text-dim)" fontSize="9" fontFamily="JetBrains Mono, monospace" textAnchor="middle">POSITION</text>
              </svg>
            </div>
          </div>

          {/* Error code */}
          <h1 className="text-7xl sm:text-8xl font-black tracking-tighter mb-2 font-mono"
            style={{
              background: "linear-gradient(135deg, var(--iq-red), #ff6b6b)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            404
          </h1>

          {/* Error title */}
          <h2 className="text-lg sm:text-xl font-bold text-white mb-2 tracking-tight">
            {t("not_found_title")}
          </h2>
          <p className="text-sm text-muted max-w-xs mx-auto mb-2 leading-relaxed">
            {t("not_found_desc")}
          </p>

          {/* Market data — all showing dashes */}
          <div className="max-w-xs mx-auto mt-6 mb-8 p-3 rounded-lg" style={{ background: "var(--iq-panel)", border: "1px solid var(--iq-border)" }}>
            <div className="flex justify-between items-center text-[10px] font-mono py-1" style={{ color: "var(--iq-text-dim)" }}>
              <span>Symbol</span>
              <span>Bid</span>
              <span>Ask</span>
              <span>Change</span>
            </div>
            <div className="flex justify-between items-center text-[11px] font-mono py-1.5 border-t border-line/50" style={{ color: "var(--iq-text-dim)" }}>
              <span className="text-white/40">404</span>
              <span className="text-red/40">---</span>
              <span className="text-red/40">---</span>
              <span className="text-red/40">-100%</span>
            </div>
            <div className="flex justify-between items-center text-[11px] font-mono py-1.5 border-t border-line/50" style={{ color: "var(--iq-text-dim)" }}>
              <span className="text-white/30">N/F</span>
              <span className="text-red/30">---</span>
              <span className="text-red/30">---</span>
              <span className="text-red/30">-100%</span>
            </div>
            <div className="flex justify-between items-center text-[11px] font-mono py-1.5 border-t border-line/50" style={{ color: "var(--iq-text-dim)" }}>
              <span className="text-white/20">???</span>
              <span className="text-red/20">---</span>
              <span className="text-red/20">---</span>
              <span className="text-red/20">-100%</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/trade"
              className="touch-target w-full sm:w-auto inline-flex items-center justify-center gap-2 font-bold px-6 py-3 rounded-lg uppercase tracking-widest text-sm transition-all duration-200"
              style={{
                background: "linear-gradient(135deg, var(--iq-green) 0%, #00c853 100%)",
                color: "#000",
                boxShadow: "0 4px 20px rgba(0,230,118,0.25)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 6px 30px rgba(0,230,118,0.4)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,230,118,0.25)"; }}
            >
              <BarChart3 className="w-4 h-4" />
              {t("not_found_trade")}
            </Link>
            <Link
              to="/"
              className="touch-target w-full sm:w-auto inline-flex items-center justify-center gap-2 font-medium px-6 py-3 rounded-lg text-sm transition-all duration-200 border"
              style={{
                borderColor: "var(--iq-border-bright)",
                color: "var(--iq-text)",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--iq-text-dim)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--iq-border-bright)"; e.currentTarget.style.color = "var(--iq-text)"; }}
            >
              <Home className="w-4 h-4" />
              {t("not_found_home")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
